import React from "react"
import { ConnectedRoute as Route } from "./lib/connectedRouting"
import { Switch } from "react-router-dom"
import { ConnectedRouter as Router } from "react-router-redux"
import { ApolloProvider } from "react-apollo"
import { Provider } from "react-redux"
import PropTypes from "prop-types"
import storeWithHistory from "./store"
import apolloClient from "./lib/apolloClient"
import DefaultRoute from "./routes/DefaultRoute"
import SearchView from "./routes/SearchView"
import ReportView from "./routes/ReportView"
import AlertsView from "./routes/AlertsView"
import SearchPageView from "./routes/SearchPageView"
import UtilizationView from "./routes/UtilizationView"
import UtilizationDetailView from "./routes/UtilizationDetailView"
import ContextualHeader from "./components/Header/ContextualHeader"
import { ErrorBoundary, NotFound } from "@swipesense/error-handling"
import routes from "./routes"
import MediaQueryWithDefaultValues from "../src/components/MediaQueryWithDefaultValues"
import StatusPageIncidentAlert from "./components/StatusPageIncidentAlert"

/* istanbul ignore next */
if (process.env.NODE_ENV === "production")
  window.Raven.config(process.env.REACT_APP_SENTRY_RAVEN_KEY, {
    environment: process.env.REACT_APP_STAGE,
    release: process.env.REACT_APP_SENTRY_RELEASE,
  }).install()

/* breakpoint constants
NOTE: Mobile and tablet constants are different than Bootstrap defaults
because we needed to take out landscape mode due to keyboard issues, however
we need the layout to look different on landscape mode. So with these constants,
a phone in landscape will have a tablet layout.
*/
const MOBILE_MAX_WIDTH = 567
const TABLET_MIN_WIDTH = 568
const TABLET_MAX_WIDTH = 992
const DESKTOP_MIN_WIDTH = 993

export const FunctionalApp = props => {
  return (
    <div className="App">
      <ContextualHeader />
      <ErrorBoundary>
        <Switch>
          <Route path={routes.root} exact component={DefaultRoute} />
          <Route
            path={routes.facility}
            exact
            render={routeProps => (
              <SearchPageView
                deviceType={props.deviceType}
                match={routeProps.match}
              />
            )}
          />
          <Route
            path={routes.assets}
            exact
            render={routeProps => (
              <SearchView
                deviceType={props.deviceType}
                match={routeProps.match}
              />
            )}
          />
          <Route
            path={routes.reports}
            render={routeProps => (
              <ReportView
                deviceType={props.deviceType}
                match={routeProps.match}
                location={routeProps.location}
              />
            )}
          />
          <Route
            path={routes.alerts}
            render={routeProps => (
              <AlertsView
                deviceType={props.deviceType}
                match={routeProps.match}
              />
            )}
          />
          <Route
            path={routes.utilizationDetail}
            render={routeProps => (
              <UtilizationDetailView
                deviceType={props.deviceType}
                match={routeProps.match}
              />
            )}
          />
          <Route
            path={routes.utilization}
            render={routeProps => (
              <UtilizationView
                deviceType={props.deviceType}
                match={routeProps.match}
              />
            )}
          />
          <Route component={NotFound} />
        </Switch>
        <StatusPageIncidentAlert />
      </ErrorBoundary>
    </div>
  )
}

FunctionalApp.propTypes = {
  deviceType: PropTypes.string.isRequired,
}

FunctionalApp.defaultProps = {
  deviceType: "desktop",
}

const { store, history } = storeWithHistory()

class App extends React.Component {
  render() {
    return (
      <Provider store={store}>
        <ApolloProvider client={apolloClient}>
          <Router history={history}>
            <div className="asset-tracking">
              {/* MOBILE */}
              <MediaQueryWithDefaultValues maxWidth={MOBILE_MAX_WIDTH}>
                <div className="mobile portrait">
                  <FunctionalApp deviceType={"mobile"} />
                </div>
                {/* Do not have a landscape class because when the keyboard
                  appears on Android, it thinks it's in landscape
                */}
              </MediaQueryWithDefaultValues>

              {/* TABLET */}
              <MediaQueryWithDefaultValues
                minWidth={TABLET_MIN_WIDTH}
                maxWidth={TABLET_MAX_WIDTH}
              >
                <div className="tablet">
                  <FunctionalApp deviceType={"tablet"} />
                </div>
              </MediaQueryWithDefaultValues>

              {/* DESKTOP */}
              <MediaQueryWithDefaultValues minWidth={DESKTOP_MIN_WIDTH}>
                <div className="desktop">
                  <FunctionalApp deviceType={"desktop"} />
                </div>
              </MediaQueryWithDefaultValues>
            </div>
          </Router>
        </ApolloProvider>
      </Provider>
    )
  }
}

export default App
